import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Sparkles } from 'lucide-react';

import { useTranslation } from '@sshop/i18n';
import { cn, Container } from '@sshop/ui';

import { useLocale } from '@/shared/hooks/useLocale';
import { routes } from '@/shared/lib/routes';
import { ThemeToggle } from '@/shared/ui/ThemeToggle';

import { AccountMenu } from '../AccountMenu';

import { Brand } from './Brand/Brand';
import { CartButton } from './CartButton';
import { CategoriesDesktopMenu, CategoriesMobileMenu } from './CategoriesMenu';
import { CompareButton } from './CompareButton';
import { HeaderSearch } from './HeaderSearch/HeaderSearch';
import { LanguageSwitcher } from './LanguageSwitcher';
import { WishlistButton } from './WishlistButton';
import { headerStyles as s } from './Header.styles';

export function Header() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [searchFocused, setSearchFocused] = useState(false);

  return (
    <header className={s.root}>
      <Container className={s.container}>
        <Link to={routes.home(locale)} className={s.brand} aria-label="SSHOP — home">
          <Brand />
        </Link>

        <div className={s.actions}>
          <CategoriesMobileMenu />
          <CartButton />
          <div className={s.actionsCompare}>
            <CompareButton />
          </div>
          <WishlistButton />
          <div className={s.desktopAction}>
            <ThemeToggle />
          </div>
          <div className={s.desktopAction}>
            <LanguageSwitcher />
          </div>
          <div className={s.divider} aria-hidden />
          <AccountMenu />
        </div>

        <div className={s.search}>
          <Link
            to={routes.catalog(locale)}
            className={cn(s.mobileNavLink, searchFocused && s.mobileNavLinkFocused)}
            aria-label={t('nav.allProducts')}
          >
            <ShoppingBag className="h-4 w-4 shrink-0" aria-hidden />
            <span
              className={cn(
                'sr-only sm:not-sr-only',
                s.navLabel,
                searchFocused && s.navLabelCollapsed,
              )}
            >
              {t('nav.allProducts')}
            </span>
          </Link>
          <Link
            to={`${routes.catalog(locale)}?special_price=1`}
            className={cn(s.mobileNavLink, searchFocused && s.mobileNavLinkFocused)}
            aria-label={t('nav.deals')}
          >
            <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
            <span
              className={cn(
                'sr-only sm:not-sr-only',
                s.navLabel,
                searchFocused && s.navLabelCollapsed,
              )}
            >
              {t('nav.deals')}
            </span>
          </Link>
          <div className={s.mobileCompare}>
            <CompareButton />
          </div>
          <div className={s.searchField}>
            <HeaderSearch onFocusChange={setSearchFocused} />
          </div>
        </div>

        <nav className={s.nav} aria-label="Primary">
          <CategoriesDesktopMenu />
          <Link
            to={routes.catalog(locale)}
            className={cn(s.dealsLink, searchFocused && s.dealsLinkFocused)}
            aria-label={t('nav.allProducts')}
          >
            <ShoppingBag className="h-4 w-4 shrink-0" aria-hidden />
            <span className={cn(s.navLabel, searchFocused && s.navLabelCollapsed)}>
              {t('nav.allProducts')}
            </span>
          </Link>
          <Link
            to={`${routes.catalog(locale)}?special_price=1`}
            className={cn(s.dealsLink, searchFocused && s.dealsLinkFocused)}
            aria-label={t('nav.deals')}
          >
            <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
            <span className={cn(s.navLabel, searchFocused && s.navLabelCollapsed)}>
              {t('nav.deals')}
            </span>
          </Link>
        </nav>
      </Container>
    </header>
  );
}
